import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import deliveryFleetService from '../../services/deliveryFleetService';
import customerOrderService from '../../services/customerOrderService';
import { usePermissions } from '../../context/PermissionContext';
import 'mapbox-gl/dist/mapbox-gl.css';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const MAP_POLL_MS = 15000;

// Drivers only report GPS while actively on a picked_up delivery (see PortalDriverDeliveriesPage.js
// — the location ping only runs during that status). A driver who's "available" but idle has no
// live position on file at all; this is a known, intentional gap (continuous background tracking
// while merely available is Phase 2 — battery/privacy tradeoffs, see docs/DELIVERY_FLEET_MODULE.md).
function FleetMapView({ activeDeliveries, drivers }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  const located = activeDeliveries.filter(d => d.status === 'picked_up' && d.currentLat != null && d.currentLng != null);
  const locatedDriverIds = new Set(located.map(d => d.Driver?.id).filter(Boolean));
  const availableDrivers = drivers.filter(d => d.isAvailableForDelivery);
  const idleAvailable = availableDrivers.filter(d => !locatedDriverIds.has(d.id));

  useEffect(() => {
    if (!MAPBOX_TOKEN) return;
    let cancelled = false;
    import('mapbox-gl').then((mapboxgl) => {
      if (cancelled || !containerRef.current) return;
      mapboxgl.default.accessToken = MAPBOX_TOKEN;
      if (!mapRef.current) {
        mapRef.current = new mapboxgl.default.Map({
          container: containerRef.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: located[0] ? [located[0].currentLng, located[0].currentLat] : [-80.19, 25.77],
          zoom: located.length ? 11 : 9,
        });
      }
      markersRef.current.forEach(m => m.remove());
      markersRef.current = [];
      const map = mapRef.current;
      const bounds = new mapboxgl.default.LngLatBounds();
      located.forEach(d => {
        const el = document.createElement('div');
        el.style.cssText = 'background:#0d6efd;color:#fff;border-radius:50%;width:32px;height:32px;display:flex;align-items:center;justify-content:center;font-size:16px;box-shadow:0 1px 4px rgba(0,0,0,.4);border:2px solid #fff;';
        el.textContent = '🚗';
        const popupHtml = `<strong>${d.Driver?.firstName || ''} ${d.Driver?.lastName || ''}</strong><br/>#${d.Order?.orderNumber || ''}<br/>${d.Order?.deliveryCity || ''}`;
        const marker = new mapboxgl.default.Marker({ element: el })
          .setLngLat([d.currentLng, d.currentLat])
          .setPopup(new mapboxgl.default.Popup({ offset: 20 }).setHTML(popupHtml))
          .addTo(map);
        markersRef.current.push(marker);
        bounds.extend([d.currentLng, d.currentLat]);
      });
      if (located.length > 1) map.fitBounds(bounds, { padding: 60, maxZoom: 13 });
      else if (located.length === 1) map.setCenter([located[0].currentLng, located[0].currentLat]);
    });
    return () => { cancelled = true; };
  }, [located]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="card">
        <div className="card-body text-center text-muted py-5">
          Mapbox isn't configured on this environment yet — the live fleet map needs
          <code className="mx-1">REACT_APP_MAPBOX_TOKEN</code> set.
        </div>
      </div>
    );
  }

  return (
    <div className="row g-3">
      <div className="col-md-8">
        <div ref={containerRef} className="card" style={{ height: 480, overflow: 'hidden' }} />
      </div>
      <div className="col-md-4">
        <div className="card h-100">
          <div className="card-header" style={{ backgroundColor: '#1b2638', color: 'white' }}>Available Drivers ({availableDrivers.length})</div>
          <div className="card-body p-0" style={{ maxHeight: 480, overflowY: 'auto' }}>
            {availableDrivers.length === 0 ? (
              <p className="text-muted fst-italic p-3 mb-0">No drivers are currently available.</p>
            ) : (
              <ul className="list-group list-group-flush">
                {located.map(d => (
                  <li key={`loc-${d.id}`} className="list-group-item d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-semibold small">🚗 {d.Driver?.firstName} {d.Driver?.lastName}</div>
                      <div className="text-muted" style={{ fontSize: 11 }}>On delivery — #{d.Order?.orderNumber}</div>
                    </div>
                    <span className="badge bg-primary">Live</span>
                  </li>
                ))}
                {idleAvailable.map(d => (
                  <li key={`idle-${d.id}`} className="list-group-item d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-semibold small">{d.firstName} {d.lastName}</div>
                      <div className="text-muted" style={{ fontSize: 11 }}>Available, no active delivery</div>
                    </div>
                    <span className="badge bg-secondary">No live location</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Non-Shopify orders have no delivery address of their own — CustomerOrder.deliveryAddressLine1
// etc. are only ever populated by the Shopify webhook. Falls back to the wholesale Customer's own
// (single-string) address/phone as a starting point; always editable regardless of source.
function buildDispatchAddressForm(order) {
  const hasOwnAddress = !!order.deliveryAddressLine1;
  return {
    deliveryContactName: order.deliveryContactName || order.Customer?.name || '',
    deliveryPhone: order.deliveryPhone || order.Customer?.phone || '',
    deliveryAddressLine1: hasOwnAddress ? order.deliveryAddressLine1 : (order.Customer?.address || ''),
    deliveryAddressLine2: order.deliveryAddressLine2 || '',
    deliveryCity: order.deliveryCity || '',
    deliveryState: order.deliveryState || '',
    deliveryZip: order.deliveryZip || '',
  };
}

function DispatchModal({ order, onClose, onDispatched }) {
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState(() => buildDispatchAddressForm(order || {}));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (order) setAddress(buildDispatchAddressForm(order));
  }, [order]);

  if (!order) return null;

  const setField = (k, v) => setAddress(a => ({ ...a, [k]: v }));

  const handleDispatch = async () => {
    setSaving(true); setError('');
    try {
      await deliveryFleetService.dispatchOrder(order.id, {
        payoutAmountUsd: amount ? Number(amount) : undefined,
        ...address,
      });
      onDispatched();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to dispatch.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1060 }} onClick={onClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1070 }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 460 }}>
          <div className="modal-content">
            <div className="modal-header"><h5 className="modal-title">Dispatch #{order.orderNumber}</h5><button className="btn-close" onClick={onClose} /></div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

              <label className="form-label small mb-1">Delivery Address</label>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <input className="form-control form-control-sm" placeholder="Contact name"
                    value={address.deliveryContactName} onChange={e => setField('deliveryContactName', e.target.value)} />
                </div>
                <div className="col-6">
                  <input className="form-control form-control-sm" placeholder="Phone"
                    value={address.deliveryPhone} onChange={e => setField('deliveryPhone', e.target.value)} />
                </div>
                <div className="col-12">
                  <input className="form-control form-control-sm" placeholder="Address line 1"
                    value={address.deliveryAddressLine1} onChange={e => setField('deliveryAddressLine1', e.target.value)} />
                </div>
                <div className="col-12">
                  <input className="form-control form-control-sm" placeholder="Address line 2 (optional)"
                    value={address.deliveryAddressLine2} onChange={e => setField('deliveryAddressLine2', e.target.value)} />
                </div>
                <div className="col-5">
                  <input className="form-control form-control-sm" placeholder="City"
                    value={address.deliveryCity} onChange={e => setField('deliveryCity', e.target.value)} />
                </div>
                <div className="col-4">
                  <input className="form-control form-control-sm" placeholder="State"
                    value={address.deliveryState} onChange={e => setField('deliveryState', e.target.value)} />
                </div>
                <div className="col-3">
                  <input className="form-control form-control-sm" placeholder="Zip"
                    value={address.deliveryZip} onChange={e => setField('deliveryZip', e.target.value)} />
                </div>
              </div>

              <label className="form-label small mb-1">Driver Payout ($)</label>
              <input type="number" min="0" step="0.01" className="form-control" placeholder="Leave blank to use company default" value={amount} onChange={e => setAmount(e.target.value)} />
              <p className="text-muted small mt-2 mb-0">This will be pushed to every available driver — first to accept gets it.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleDispatch} disabled={saving}>{saving ? 'Dispatching…' : 'Dispatch to Fleet'}</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function DispatchBoardPage() {
  const navigate = useNavigate();
  const { hasModule } = usePermissions();
  const [view, setView] = useState('board'); // 'board' | 'map'
  const [analytics, setAnalytics] = useState(null);
  const [readyOrders, setReadyOrders] = useState([]);
  const [activeDeliveries, setActiveDeliveries] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dispatchOrder, setDispatchOrder] = useState(null);

  const load = useCallback(() => {
    Promise.all([
      deliveryFleetService.getAnalytics(),
      // Scoped to direct-to-consumer orders — this is a proactive "needs a driver" worklist, not
      // every order that happens to be in an eligible status. Widening the status list alone
      // (ready/shipped/invoiced, matching the backend's own dispatch eligibility) without this
      // filter flooded the board with every historical wholesale invoiced/shipped order, which
      // was never going to go through the fleet. Dispatching a one-off wholesale order is still
      // possible from AdminOrdersPage.js's per-order "Dispatch to Fleet" action — once dispatched
      // it shows up here too, under Active Deliveries.
      // archived:'false' was missing entirely — an order someone archived (end-of-lifecycle
      // cleanup) without it ever going through the fleet (delivered another way, or an event order
      // that didn't need delivery) stayed in this worklist forever, since nothing here excludes
      // archived rows the way AdminOrdersPage.js's Active/History tabs do.
      customerOrderService.getOrders({ status: 'ready,shipped,invoiced', isDirectToConsumer: true, archived: 'false' }),
      // Includes 'delivered' too, so unpaid-but-completed deliveries stay visible for the
      // Mark Paid action — filtered back out below once paid, so the board stays "needs attention".
      deliveryFleetService.listDeliveries('offered,accepted,picked_up,delivered'),
      deliveryFleetService.listDrivers(),
    ]).then(([a, orders, dels, drvs]) => {
      setAnalytics(a);
      const active = dels.filter(d => d.status !== 'delivered' || d.payoutStatus === 'pending');
      const dispatchedOrderIds = new Set(dels.map(d => d.orderId));
      setReadyOrders(orders.filter(o => !dispatchedOrderIds.has(o.id)));
      setActiveDeliveries(active);
      setDrivers(drvs);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Keep driver positions fresh while the map tab is open
  useEffect(() => {
    if (view !== 'map') return;
    const interval = setInterval(load, MAP_POLL_MS);
    return () => clearInterval(interval);
  }, [view, load]);

  const handleCancelOffer = async (deliveryId) => {
    await deliveryFleetService.cancelOffer(deliveryId);
    load();
  };

  const handleMarkPaid = async (deliveryId) => {
    await deliveryFleetService.markPaid(deliveryId);
    load();
  };

  if (loading) return <div className="text-center py-5"><span className="spinner-border" /></div>;

  return (
    <div className="container mt-4 mb-5" style={{ maxWidth: 1000 }}>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="mb-0">Delivery Dispatch Board</h4>
        {hasModule('deliveryfleet.drivers') && (
          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/delivery-fleet/drivers')}>👤 Drivers</button>
        )}
      </div>

      {analytics && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <div className="card text-center"><div className="card-body py-3">
              <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>Deliveries</div>
              <div className="fw-bold" style={{ fontSize: 20 }}>{analytics.totalDeliveries}</div>
            </div></div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center"><div className="card-body py-3">
              <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>Paid to Drivers</div>
              <div className="fw-bold text-success" style={{ fontSize: 20 }}>{money(analytics.totalPaidToDrivers)}</div>
            </div></div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center"><div className="card-body py-3">
              <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>PV Fees</div>
              <div className="fw-bold" style={{ fontSize: 20 }}>{money(analytics.totalPvFees)}</div>
            </div></div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card text-center"><div className="card-body py-3">
              <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>Avg Delivery</div>
              <div className="fw-bold" style={{ fontSize: 20 }}>{analytics.avgDeliveryMinutes != null ? `${analytics.avgDeliveryMinutes}m` : '—'}</div>
            </div></div>
          </div>
        </div>
      )}

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${view === 'board' ? 'active' : ''}`} onClick={() => setView('board')}>Board</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${view === 'map' ? 'active' : ''}`} onClick={() => setView('map')}>🗺️ Fleet Map</button>
        </li>
      </ul>

      {view === 'map' ? (
        <FleetMapView activeDeliveries={activeDeliveries} drivers={drivers} />
      ) : (
        <>
          <div className="card mb-4">
            <div className="card-header" style={{ backgroundColor: '#1b2638', color: 'white' }}>Ready for Dispatch</div>
            <div className="card-body p-0">
              {readyOrders.length === 0 ? (
                <p className="text-muted fst-italic p-3 mb-0">No orders waiting to be dispatched.</p>
              ) : (
                <table className="table table-sm mb-0">
                  <thead className="table-light"><tr><th>Order</th><th>Delivery To</th><th></th></tr></thead>
                  <tbody>
                    {readyOrders.map(o => (
                      <tr key={o.id}>
                        <td className="small">#{o.orderNumber}</td>
                        <td className="small">{o.deliveryContactName || o.Customer?.name || '—'} {o.deliveryCity ? `· ${o.deliveryCity}` : ''}</td>
                        <td><button className="btn btn-primary btn-sm" onClick={() => setDispatchOrder(o)}>🚚 Dispatch to Fleet</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header" style={{ backgroundColor: '#1b2638', color: 'white' }}>Active Deliveries</div>
            <div className="card-body p-0">
              {activeDeliveries.length === 0 ? (
                <p className="text-muted fst-italic p-3 mb-0">No active deliveries.</p>
              ) : (
                <table className="table table-sm mb-0">
                  <thead className="table-light"><tr><th>Order</th><th>Driver</th><th>Status</th><th className="text-end">Payout</th><th></th></tr></thead>
                  <tbody>
                    {activeDeliveries.map(d => (
                      <tr key={d.id}>
                        <td className="small">#{d.Order?.orderNumber}</td>
                        <td className="small">{d.Driver ? `${d.Driver.firstName} ${d.Driver.lastName}` : <span className="text-muted">Awaiting acceptance</span>}</td>
                        <td><span className="badge bg-info text-dark text-capitalize">{d.status.replace('_', ' ')}</span></td>
                        <td className="text-end small">{money(d.payoutAmountUsd)}</td>
                        <td>
                          {d.status === 'offered' && <button className="btn btn-outline-danger btn-sm" onClick={() => handleCancelOffer(d.id)}>Cancel Offer</button>}
                          {d.status === 'delivered' && d.payoutStatus === 'pending' && <button className="btn btn-outline-success btn-sm" onClick={() => handleMarkPaid(d.id)}>Mark Paid</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      <DispatchModal order={dispatchOrder} onClose={() => setDispatchOrder(null)} onDispatched={() => { setDispatchOrder(null); load(); }} />
    </div>
  );
}
